import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import Select from 'react-select'
import Configuration from './config/Configuration'
import Submit from './config/Submit'
import Address from '../general/Address'
import useEnvironment from '../../hooks/useEnvironment'
import { CHAIN_INFO } from '../../constants/networks'
import { parseName } from '../../utils/math-utils'
import { LoaderBar, Skeleton } from '../../styles/ds'
import { SafeConfigProvider } from 'src/context/SafeConfigProvider'
import { OnChainProvider, supportedChains } from 'src/context/OnChainProvider'
import { WagmiProvider } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, getDefaultConfig } from '@rainbow-me/rainbowkit'

interface DropdownOption {
  value: number
  label: string
}

type Params = {
  tab: string
  id: string
}

const Tabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('configuration')
  const [environmentAuthorizer, setEnvironmentAuthorizer] = useState<string>(null)
  const [dropdown, setDropDown] = useState<DropdownOption[]>([])
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null)
  const params = useParams<Params>()
  const tab = params.tab

  const { data: environmentData, isLoading } = useEnvironment(params.id)

  const environmentChains = useMemo(() => {
    if (!environmentData) return []
    const uniqueChainIDs = new Set<number>()
    environmentData.smartVaults.forEach((item) => uniqueChainIDs.add(item.chainId))
    return supportedChains.filter((chain) => uniqueChainIDs.has(chain.id))
  }, [environmentData])

  const dynamicWagmiConfig = useMemo(() => {
    if (environmentChains.length === 0) return null
    return getDefaultConfig({
      appName: 'mimic-env-tool',
      projectId: process.env.REACT_APP_WC_CLOUD_PROJECT_ID,
      chains: environmentChains,
      ssr: false,
      enableWalletConnect: true,
      enableInjected: true,
      enableEIP6963: true,
      enableCoinbase: true,
    })
  }, [environmentChains])

  const queryClient = useMemo(() => new QueryClient(), [])

  useEffect(() => {
    if (environmentData) {
      const uniqueChainIDs = new Set<number>()
      environmentData.smartVaults.forEach((item) => uniqueChainIDs.add(item.chainId))
      const chainObjects: DropdownOption[] = Array.from(uniqueChainIDs)
        .sort((a, b) => a - b)
        .map((chainId) => ({
          value: chainId,
          label: CHAIN_INFO[chainId] ? CHAIN_INFO[chainId].name : `Chain ${chainId}`,
        }))
      setSelectedOption(chainObjects[0])
      setDropDown(chainObjects)
    }
  }, [environmentData])

  useEffect(() => {
    if (tab) setActiveTab(tab)
  }, [tab])

  const DynamicProvider = ({ children }) => {
    if (!dynamicWagmiConfig) {
      return <LoadingWallet>Loading wallet configuration…</LoadingWallet>
    }
    return (
      <WagmiProvider config={dynamicWagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider>
            <SafeConfigProvider>{children}</SafeConfigProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    )
  }

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <PageTitle>
            {environmentData ? parseName(environmentData.namespace) : <Skeleton width={200} />}
          </PageTitle>
          {environmentData && (
            <PageMeta>
              <MetaText>{environmentData.namespace}</MetaText>
              <MetaDot>·</MetaDot>
              <Address
                address={environmentData.mimicId}
                short={true}
                showIdentity={false}
                chainId={selectedOption?.value}
              />
            </PageMeta>
          )}
        </HeaderLeft>

        <HeaderRight>
          {dropdown.length > 0 && (
            <ChainSelect
              classNamePrefix="chain"
              onChange={(opt) => setSelectedOption((opt as DropdownOption) || null)}
              options={dropdown}
              value={selectedOption}
              defaultValue={dropdown[0] ?? null}
              isSearchable={false}
            />
          )}
        </HeaderRight>
      </Header>

      <TabBar>
        {[
          { id: 'configuration', label: 'Configuration' },
          { id: 'submit', label: 'Ready to submit' },
        ].map((t) => (
          <TabBtn key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </TabBtn>
        ))}
      </TabBar>

      {isLoading && <LoaderBar />}

      {selectedOption && environmentData && (
        <Content>
          {activeTab === 'configuration' && (
            <Configuration chainId={selectedOption.value} setEnvironmentAuthorizer={setEnvironmentAuthorizer} />
          )}
          {activeTab === 'submit' && (
            <DynamicProvider>
              <Submit chainId={selectedOption.value} environmentAuthorizer={environmentAuthorizer} />
            </DynamicProvider>
          )}
        </Content>
      )}
    </Page>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HeaderRight = styled.div`
  padding-top: 4px;
`

const PageTitle = styled.h1`
  color: #ededed;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`

const PageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #777;
  font-size: 12px;
`

const MetaText = styled.span`
  color: #777;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
`

const MetaDot = styled.span`
  color: #777;
`

const ChainSelect = styled(Select)`
  .chain__control {
    min-width: 160px;
    background: #111;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    box-shadow: none;
    cursor: pointer;
    min-height: 32px;
    height: 32px;
    &:hover { border-color: rgba(255, 255, 255, 0.14); }
  }
  .chain__value-container { padding: 0 10px; }
  .chain__single-value { color: #ededed; font-size: 13px; }
  .chain__indicator-separator { display: none; }
  .chain__dropdown-indicator { color: #a1a1a1; padding: 0 8px; &:hover { color: #888; } }
  .chain__menu {
    background: #161616;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    overflow: hidden;
  }
  .chain__menu-list { padding: 4px; }
  .chain__option {
    background: transparent;
    color: #a0a0a0;
    font-size: 13px;
    border-radius: 4px;
    padding: 6px 10px;
    cursor: pointer;
    &.chain__option--is-focused { background: rgba(255,255,255,0.05); color: #ededed; }
    &.chain__option--is-selected { background: rgba(111,92,230,0.15); color: #a89aff; }
  }
`

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 0;
`

const TabBtn = styled.button<{ active: boolean }>`
  padding: 8px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? '#6f5ce6' : 'transparent')};
  margin-bottom: -1px;
  color: ${({ active }) => (active ? '#ededed' : '#555')};
  font-size: 13px;
  font-weight: ${({ active }) => (active ? '500' : '400')};
  font-family: inherit;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: ${({ active }) => (active ? '#ededed' : '#a0a0a0')}; }
`

const Content = styled.div`
  padding-top: 24px;
`

const LoadingWallet = styled.div`
  color: #777;
  font-size: 13px;
  padding: 24px 0;
`

export default Tabs
